"use client";

import React, { useEffect, useRef, useState } from "react";

const CtaBanner: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    // Backend integration point — connect to waitlist API
    setSubmitted(true);
  };

  return (
    <section
      ref={sectionRef}
      className="relative py-32 px-6 overflow-hidden"
      style={{ background: "#0D0F14" }}
    >
      {/* Beam border top */}
      <div className="beam-border-h" style={{ top: 0, bottom: "auto" }} />

      {/* Background glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse 70% 60% at 50% 50%, rgba(245,158,11,0.1) 0%, rgba(239,68,68,0.04) 40%, transparent 70%)",
        }}
      />

      {/* Grid overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "linear-gradient(to right, rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.025) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <div className="max-w-3xl mx-auto relative z-10 text-center">
        <div
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(32px)",
            transition: "all 1s cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        >
          {/* Launch countdown */}
          <div
            className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl mb-10"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full" style={{ background: "#F59E0B", animation: "dot-blink 2s ease-in-out infinite" }} />
              <span className="text-sm font-medium" style={{ color: "#9B9891" }}>Launching</span>
            </div>
            <div className="h-4 w-px" style={{ background: "rgba(255,255,255,0.1)" }} />
            <span className="text-sm font-bold" style={{ color: "#F0EDE8" }}>March 2026</span>
            <div className="h-4 w-px" style={{ background: "rgba(255,255,255,0.1)" }} />
            <span className="text-sm" style={{ color: "#5C5A56" }}>2,400+ on waitlist</span>
          </div>

          <h2
            className="font-display leading-none mb-6"
            style={{ fontSize: "clamp(48px, 8vw, 88px)", color: "#F0EDE8", letterSpacing: "-0.03em" }}
          >
            Reclaim your{" "}
            <span
              className="font-display italic"
              style={{
                background: "linear-gradient(135deg, #FBBF24 0%, #F59E0B 50%, #EF4444 100%)",
                backgroundSize: "200% auto",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
                animation: "heatmap-shimmer 5s linear infinite",
              }}
            >
              mornings.
            </span>
          </h2>

          <p className="text-lg mb-12 max-w-lg mx-auto" style={{ color: "#9B9891", lineHeight: "1.6" }}>
            Join the waitlist today and get 3 months of Pro free when we launch — plus early access to the Sovereign tier.
          </p>

          {!submitted ? (
            <form
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="flex-1 px-5 py-4 rounded-2xl text-sm outline-none transition-all duration-300"
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "#F0EDE8",
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(245,158,11,0.4)")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)")}
              />
              <button
                type="submit"
                className="px-8 py-4 rounded-2xl text-sm font-bold transition-all duration-300 glow-cta whitespace-nowrap"
                style={{
                  background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
                  color: "#0D0F14",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              >
                Join Waitlist →
              </button>
            </form>
          ) : (
            <div
              className="flex items-center justify-center gap-3 py-5 px-8 rounded-2xl max-w-md mx-auto"
              style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.2)" }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M20 6L9 17l-5-5" stroke="#22C55E" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
              <span className="text-sm font-semibold" style={{ color: "#22C55E" }}>
                You're on the list! We'll email you when we launch.
              </span>
            </div>
          )}

          <p className="mt-4 text-xs" style={{ color: "#5C5A56" }}>
            No spam. Unsubscribe any time. Your email is encrypted.
          </p>
        </div>
      </div>
    </section>
  );
};

export default CtaBanner;